import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState, forwardRef } from 'react'
import ReactQuill, { ReactQuillProps } from 'react-quill'
import {
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import 'react-quill/dist/quill.snow.css'
import axios from 'axios'
import { makeStyles } from 'tss-react/mui'
import { styled, useTheme } from '@mui/material/styles'
import MuiButton, { ButtonProps } from '@mui/material/Button'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import EditIcon from '@mui/icons-material/Edit'
import { MuiColorInput } from 'mui-color-input'
import IconTooltip from '../../components/icon-tooltip/icon-tooltip'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { fsColors } from '../../shared/constants'
import { changeCurrentReport, deleteReport, setReport, updateReport } from '../../store/actions'
import EntgeltTransP from '../../components/fs-reports/EntgeltTransP'
import FAIRModule from '../../components/fs-reports/FAIRModule'
import { reportAPI } from '../../shared/api/ReportAPI'
import { Report } from '../../shared/model/models'
import ACTIONS from '../../store/constants'
import { useLoading, useDebounce } from '../../shared/hooks'
import FsDemoAccountModal from '../../components/common/molecules/DemoAccountModal'
import { getIsDemoAccount } from '../../utils'
import { getIcon } from '../../components/common/Icons'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'

const { REPORT } = ACTIONS

type NavigationParams = {
  id?: string
}

const useStyles = makeStyles()(() => ({
  card: {
    width: '100%',
    height: 'calc(100vh - 170px)',
    borderRadius: 5,
    overflow: 'hidden',
  },
}))

const FsEditReport = () => {
  const { t } = useTranslation()
  const { id } = useParams<NavigationParams>()
  const { classes } = useStyles()
  const { currentReport } = useAppSelector((state) => state.currentReport)
  const { account } = useAppSelector((state) => state.currentUser)
  const { modules } = useAppSelector((state) => state.currentAnalysis)
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [currentScale, setCurrentScale] = useState<number>(0.7)
  const [tempCurrentPage, setTempCurrentPage] = useState<number>(1)
  const [reportState, setReportState] = useState<any>(null)
  const [curRep, setCurRep] = useState<Report | null>(null)
  const [isDirtied, setIsDirtied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [totalPages, setTotalPages] = useState(0)
  const [indexPages, setIndexPages] = useState(0)
  const navigate = useNavigate()
  const [reportPoll, setReportPoll] = useState<any>(null)
  const [firstTimeLoad, setFirstTimeLoad] = useState<boolean>(true)
  const largerThanMid = useMediaQuery(theme.breakpoints.up('md'))
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
  const [nameError, setNameError] = useState(false)
  const [showEditSummary, setShowEditSummary] = useState<boolean>(false)

  const { isFetching: isSavingChanges } = useLoading([REPORT.UPDATE_REPORT])

  useEffect(() => {
    if (!currentReport || currentReport.id !== parseInt(id ?? '', 10)) {
      dispatch(setReport({ reportId: parseInt(id ?? '', 10) }))
      setCurrentPage(1)
      setIsDirtied(false)
    }
    setFirstTimeLoad(false)
    return () => {
      if (reportPoll) {
        clearInterval(reportPoll)
        setReportPoll(null)
      }
    }
  }, [id, reportPoll, firstTimeLoad])

  useEffect(() => {
    if (currentReport) setCurRep({ ...currentReport })
  }, [currentReport])

  const Button = styled((aprops: ButtonProps) => <MuiButton {...aprops} />)(() => ({
    color: `${
      theme.palette.mode === 'dark' ? fsColors.fsDarkBlue.colors[3] : fsColors.fsDarkBlue.primary
    }`,
    border: `1px solid ${
      theme.palette.mode === 'dark' ? fsColors.fsDarkBlue.colors[3] : fsColors.fsDarkBlue.primary
    }`,
    textTransform: 'capitalize',
    '&:hover': {
      border: `1px solid ${
        theme.palette.mode === 'dark' ? fsColors.fsDarkBlue.colors[3] : fsColors.fsDarkBlue.light
      }`,
    },
  }))

  const getFileFromS3 = async (url: string) => {
    if (curRep) {
      const response = await axios({
        url,
        method: 'GET',
        responseType: 'blob',
        timeout: 180000,
      })
      if (response) {
        const blobUrl = URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `${curRep.reportName}.pdf`
        document.body.appendChild(link)
        link.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          }),
        )
        setTimeout(() => {
          document.body.removeChild(link)
        }, 100)
        setLoading(false)
      }
    }
  }

  const refreshReport = () => {
    if (curRep) {
      const repPoll = setInterval(
        () => dispatch(setReport({ reportId: parseInt(id ?? '', 10) })),
        20000,
      )
      setReportPoll(repPoll)
    }
  }

  const downloadReport = async () => {
    try {
      if (curRep) {
        setLoading(true)
        const downloadURL = await reportAPI.getReportPDF(curRep.id)
        if (downloadURL.url) {
          getFileFromS3(downloadURL.url)
        } else {
          refreshReport()
        }
      }
    } catch (error: any) {
      setReportState({
        message: error.response.statusText,
        severity: 'error',
      })
    }
  }

  useEffect(() => {
    if (curRep?.pdfGenerationStatus !== 'in_progress' && reportPoll) {
      clearInterval(reportPoll)
      setReportPoll(null)
      if (curRep?.pdfGenerationStatus === 'failed') {
        setReportState({
          message: t('pdf.generation.failed'),
          severity: 'error',
        })
      }
      if (curRep?.pdfGenerationStatus === 'complete') downloadReport()
      else setLoading(false)
    }
  }, [curRep])

  const lockReport = async () => {
    if (currentReport) {
      try {
        setLoading(true)
        const response = await reportAPI.lockReport(currentReport.id, !currentReport.isEditable)
        if (response === 200) {
          dispatch(
            changeCurrentReport({
              ...currentReport,
              isEditable: !currentReport.isEditable,
            }),
          )
          setCurRep({
            ...currentReport,
            isEditable: !currentReport.isEditable,
          })
        }
      } catch (error: any) {
        setReportState({
          message: error.response.statusText,
          severity: 'error',
        })
      } finally {
        setLoading(false)
      }
    }
  }

  const renameReport = async (name: string) => {
    if (currentReport) {
      try {
        const response = await reportAPI.renameReport(currentReport.id, name)
        if (response === 200) {
          setIsDirtied(true)
          dispatch(
            changeCurrentReport({
              ...currentReport,
              reportName: name,
            }),
          )
          setCurRep({
            ...currentReport,
            reportName: name,
          })
        }
      } catch (error: any) {
        setReportState({
          message: error.response.statusText,
          severity: 'error',
        })
      }
    }
  }

  const deleteCurReport = async () => {
    if (curRep) {
      setDeleting(true)
      await dispatch(deleteReport({ reportId: curRep.id }))
      setDeleting(false)
      navigate('/fs/reports')
    }
  }

  const curPageData = curRep?.reportData?.pages
    ? curRep?.reportData?.pages[currentPage - indexPages - 2]
    : {}

  const changePage = (pageNumber: number) => {
    setCurrentPage(pageNumber < 1 ? 1 : pageNumber > totalPages ? totalPages : pageNumber)
    setTempCurrentPage(pageNumber < 1 ? 1 : pageNumber > totalPages ? totalPages : pageNumber)
  }

  const debouncedRequest = useDebounce(() => {
    if (curRep) {
      setIsDirtied(true)
      dispatch(updateReport({ report: curRep }))
    }
  })

  const onDataChange = (changes: Report) => {
    setCurRep(changes)
    debouncedRequest()
  }

  const getCurrentTemplate = () => {
    if (curRep) {
      switch (curRep.reportType) {
        case 'legal':
          return (
            <EntgeltTransP
              data={curRep}
              pageNumber={currentPage}
              scale={currentScale}
              setIndexPages={setIndexPages}
              setTotalPages={setTotalPages}
            />
          )
        default:
          return modules?.map((m) => m.name).includes(curRep.reportModule) ? (
            <FAIRModule
              data={curRep}
              pageNumber={currentPage}
              scale={currentScale}
              setIndexPages={setIndexPages}
              setTotalPages={setTotalPages}
              setCurrentPage={changePage}
              showEditSummary={showEditSummary}
              setShowEditSummary={setShowEditSummary}
              setIsDirtied={setIsDirtied}
            />
          ) : null
      }
    }
    return null
  }

  const editSummary = () => {
    changePage(indexPages + 2)
    setShowEditSummary(true)
  }

  const showEditGrid = currentReport?.isEditable && currentReport.reportType.includes('legal')

  const style = {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: fsColors.fsDarkBlue.primary,
      },
    },
  }

  const isDemoAccount = getIsDemoAccount()

  return largerThanMid ? (
    <>
      {curRep ? (
        isDemoAccount ? (
          <FsDemoAccountModal isOpen />
        ) : (
          <div>
            <Snackbar
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              open={!!reportState}
              onClose={() => setReportState(null)}
            >
              <Alert
                onClose={() => setReportState(null)}
                severity={reportState?.severity}
                sx={{ width: '100%' }}
              >
                {reportState?.message}
              </Alert>
            </Snackbar>
            <Stack mb={2} direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center">
                <Box
                  pr={2}
                  pt={0.5}
                  sx={{
                    justifyContent: 'center',
                    '& svg': {
                      fontSize: 38,
                    },
                  }}
                >
                  {getIcon('reports', { color: theme.palette.primary.main })}
                </Box>
                {currentReport?.isEditable ? (
                  <TextField
                    hiddenLabel
                    id="filled-hidden-label-small"
                    defaultValue={currentReport?.reportName}
                    size="small"
                    error={nameError}
                    onChange={(event) => {
                      if (event.target.value) setNameError(false)
                      else setNameError(true)
                    }}
                    helperText={nameError ? t('cant.empty') : ''}
                    onBlur={(event) => event.target.value && renameReport(event.target.value)}
                    sx={{ ...style, width: '500px' }}
                  />
                ) : (
                  <Typography variant="h6">{currentReport?.reportName}</Typography>
                )}
              </Stack>
              <Box py={1} display="flex">
                {account?.isSuperuser && (
                  <IconTooltip
                    title={t(`${currentReport?.isEditable ? '' : 'un'}lock.button.explain`)}
                    placement="bottom"
                    arrow
                  >
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        startIcon={currentReport?.isEditable ? <LockIcon /> : <LockOpenIcon />}
                        disabled={isSavingChanges}
                        onClick={lockReport}
                      >
                        {currentReport?.isEditable ? t('lock') : t('unlock')}
                      </Button>
                    </span>
                  </IconTooltip>
                )}
                {!account?.isSuperuser && !currentReport?.isEditable && (
                  <IconTooltip title={t('lock.icon.explain')} placement="top" arrow>
                    <span>
                      <Box mr={2} pt={0.5} mb={-0.5} color={fsColors.fsGray.colors[1]}>
                        <LockIcon />
                      </Box>
                    </span>
                  </IconTooltip>
                )}
                <IconTooltip
                  title={t(
                    `download.${
                      currentReport?.pdfGenerationStatus === 'failed' ? 'failed.' : ''
                    }button.explain`,
                  )}
                  placement="bottom"
                  arrow
                >
                  <div>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1 }}
                      startIcon={<DownloadIcon />}
                      onClick={downloadReport}
                      disabled={isSavingChanges}
                    >
                      {t('download')}
                    </Button>
                  </div>
                </IconTooltip>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    mr: 1,
                    color: `${
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.dark
                    }`,
                    border: `1px solid ${
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.dark
                    }`,
                  }}
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setOpenDeleteConfirm(true)
                  }}
                  disabled={isSavingChanges || !currentReport?.isEditable}
                >
                  {t('delete')}
                </Button>
              </Box>
            </Stack>
            <Grid
              container
              className={classes.card}
              style={{ border: `${fsColors.fsDarkBlue.colors[2]} 4px solid` }}
            >
              {showEditGrid && (
                <Grid
                  size={{ xs: 3 }}
                  sx={{
                    borderRight: `2px solid ${fsColors.fsDarkBlue.colors[2]}`,
                    height: '100%',
                    overflowY: 'scroll',
                  }}
                >
                  <Box
                    px={2}
                    py={1}
                    sx={{
                      borderBottom: `4px solid ${fsColors.fsDarkBlue.colors[2]}`,
                      display: 'flex',
                      direction: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="h6">{t('theme')}</Typography>
                  </Box>
                  <Box
                    p={2}
                    pb={0}
                    sx={{ borderBottom: `4px solid ${fsColors.fsDarkBlue.colors[2]}` }}
                  >
                    {Object.keys(curRep?.reportTheme.colors).map((c) => (
                      <div key={c}>
                        <Stack
                          direction="row"
                          spacing={1}
                          mb={1}
                          alignItems="center"
                          justifyContent="space-between"
                          key={`color${c}`}
                        >
                          <Typography width={180}>{t(`${c}.color` as any)}</Typography>
                          <MuiColorInput
                            size="small"
                            fullWidth
                            format="hex"
                            value={curRep?.reportTheme.colors[c]}
                            isAlphaHidden
                            onChange={(_, colors) =>
                              onDataChange({
                                ...curRep,
                                reportTheme: {
                                  ...curRep.reportTheme,
                                  colors: { ...curRep.reportTheme.colors, [c]: colors.hex },
                                },
                              })
                            }
                          />
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          mb={2}
                          alignItems="center"
                          justifyContent="space-between"
                          key={`colorreset${c}`}
                        >
                          <Typography width={100}>{}</Typography>
                          <Typography
                            variant="caption"
                            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={() => {
                              onDataChange({
                                ...curRep,
                                reportTheme: {
                                  ...curRep.reportTheme,
                                  colors: {
                                    ...curRep.reportTheme.colors,
                                    [c]: currentReport.reportTheme.originalColors[c],
                                  },
                                },
                              })
                            }}
                          >
                            {t('reset.to.original')}
                          </Typography>
                        </Stack>
                      </div>
                    ))}
                  </Box>
                  <Box
                    px={2}
                    py={1}
                    sx={{
                      borderBottom: `4px solid ${fsColors.fsDarkBlue.colors[2]}`,
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="h6">
                      {t('edit')} - {t('page')} {currentPage}
                    </Typography>
                  </Box>
                  <Box p={2} sx={{ height: 'calc(100% - 292px)', minHeight: '500px' }}>
                    {curPageData?.isEditable ? (
                      <>
                        <Typography mb={1}>{t('title')}</Typography>
                        <TextField
                          id="page-number"
                          size="small"
                          value={curPageData.title}
                          fullWidth
                          onChange={(event) => {
                            const newPages = [...curRep.reportData.pages]
                            newPages[currentPage - indexPages - 2] = {
                              ...newPages[currentPage - indexPages - 2],
                              title: event.target.value,
                            }
                            onDataChange({
                              ...curRep,
                              reportData: { ...curRep.reportData, pages: newPages },
                            })
                          }}
                        />
                        <Typography mt={4} mb={1}>
                          {t('content')}
                        </Typography>
                        <ReactQuill
                          theme="snow"
                          modules={{
                            toolbar: [
                              [{ header: [1, 2, 3, false] }],
                              ['bold', 'underline', 'strike'],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              [{ color: [] }, { background: [] }],
                            ],
                            clipboard: {
                              matchVisual: false,
                            },
                          }}
                          value={curPageData.content}
                          onChange={(value) => {
                            const newPages = [...curRep.reportData.pages]
                            newPages[currentPage - indexPages - 2] = {
                              ...newPages[currentPage - indexPages - 2],
                              content: value,
                            }
                            onDataChange({
                              ...curRep,
                              reportData: { ...curRep.reportData, pages: newPages },
                            })
                          }}
                        />
                      </>
                    ) : (
                      <Typography>{t('no.editable.elements')}</Typography>
                    )}
                  </Box>
                </Grid>
              )}
              <Grid size={{ xs: showEditGrid ? 9 : 12 }} sx={{ height: '100%' }}>
                <Box
                  px={2}
                  py={1}
                  sx={{ borderBottom: `4px solid ${fsColors.fsDarkBlue.colors[2]}` }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack>
                    <Stack direction="row" alignItems="flex-end">
                      <Typography variant="h6">
                        {t('preview')} {t('on')} {t('page')} {currentPage} {t('of')} {totalPages}
                      </Typography>
                      <Typography variant="caption" ml={2} mb={0.45}>
                        {currentReport?.isEditable &&
                          (isSavingChanges
                            ? t('saving.changes')
                            : isDirtied
                            ? t('changes.saved')
                            : '')}
                      </Typography>
                    </Stack>
                    {!currentReport?.isEditable ? (
                      <Stack direction="row" alignItems="center">
                        <LockIcon
                          color="disabled"
                          fontSize="small"
                          sx={{ ml: -0.5, mr: 0.5, mt: -0.65 }}
                        />
                        <Typography variant="caption">{t('no.editable.report')}</Typography>
                      </Stack>
                    ) : currentReport.reportType === 'general' ? (
                      <Typography variant="caption">{t('general.report')}</Typography>
                    ) : null}
                  </Stack>
                  <Stack direction="row" alignItems="center" mt={-0.25}>
                    {currentReport?.reportType === 'general' && currentReport?.isEditable && (
                      <Button
                        startIcon={<EditIcon fontSize="small" />}
                        size="small"
                        sx={{ mr: 2 }}
                        onClick={editSummary}
                        disabled={isSavingChanges}
                      >
                        <Typography variant="caption">{t('edit.summary')}</Typography>
                      </Button>
                    )}
                    <Button
                      startIcon={<ZoomOutIcon />}
                      size="small"
                      sx={{ border: 0, ':hover': { border: 0 } }}
                      onClick={() => setCurrentScale(currentScale - 0.1)}
                      disabled={currentScale <= 0.2}
                    >
                      <Typography variant="caption">{t('zoom.out')}</Typography>
                    </Button>
                    <Button
                      startIcon={<ZoomInIcon />}
                      size="small"
                      sx={{ border: 0, ':hover': { border: 0 }, ml: 2 }}
                      onClick={() => setCurrentScale(currentScale + 0.1)}
                      disabled={currentScale >= 0.9}
                    >
                      <Typography variant="caption">{t('zoom.in')}</Typography>
                    </Button>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    <IconButton
                      size="small"
                      onClick={() => changePage(1)}
                      disabled={currentPage === 1}
                    >
                      <KeyboardDoubleArrowLeftIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => changePage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <TextField
                      id="page-number"
                      size="small"
                      value={tempCurrentPage}
                      sx={{
                        width: '60px',
                        height: '30px',
                        input: { fontSize: '12px', textAlign: 'center' },
                      }}
                      onChange={(event) => {
                        setTempCurrentPage(Number(event.target.value))
                      }}
                      onBlur={(event) => {
                        changePage(Number(event.target.value))
                      }}
                      onKeyDown={(event) => {
                        if (event.keyCode === 13) {
                          // @ts-ignore
                          changePage(Number(event.target.value))
                          // @ts-ignore
                          event.target.blur()
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => changePage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => changePage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <KeyboardDoubleArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    overflow: 'scroll',
                    height: 'calc(100% - 72px)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {getCurrentTemplate()}
                </Box>
              </Grid>
            </Grid>
            <Dialog
              open={openDeleteConfirm}
              onClose={() => setOpenDeleteConfirm(false)}
              aria-labelledby="responsive-dialog-title"
            >
              <DialogTitle id="responsive-dialog-title">{t('delete.confirm.title')}</DialogTitle>
              <DialogContent>
                <DialogContentText>{t('delete.confirm.message')}</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDeleteConfirm(false)}>{t('cancel')}</Button>
                <Button
                  onClick={deleteCurReport}
                  sx={{
                    color: `${
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.dark
                    }`,
                    border: `1px solid ${
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.dark
                    }`,
                  }}
                  startIcon={<DeleteIcon />}
                >
                  {t('delete')}
                </Button>
              </DialogActions>
            </Dialog>
            {deleting ? <FsFullPageLoader /> : null}
          </div>
        )
      ) : (
        <Typography variant="h6">{t('no.report')}</Typography>
      )}
      {loading || firstTimeLoad ? <FsFullPageLoader /> : null}
    </>
  ) : (
    <Typography variant="h6">{t('no.report.small.screen')}</Typography>
  )
}

export default FsEditReport
